import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { deburr } from 'lodash';

import { OrganisationsForSageLinking } from '@futrli-sdk/organisations';

import Box from 'carbon-react/lib/components/box';
import { FilterableSelect, Option } from 'carbon-react/lib/components/select';
import Message from 'carbon-react/lib/components/message';
import { RadioButton, RadioButtonGroup } from 'carbon-react/lib/components/radio-button';
import Typography from 'carbon-react/lib/components/typography';

import { SfaFormIndentation } from '../../components/sfa/sfa-form-indentation';
import { SFA_LINK_OPTIONS } from '../../sfa-types';

interface SfaLinkBusinessToOrgFormProps {
  businessName?: string;
  onSelectOption: (ev: React.ChangeEvent<HTMLInputElement>) => void;
  onSelectOrg: (ev: React.ChangeEvent<HTMLInputElement>) => void;
  linkingData?: OrganisationsForSageLinking;
  selectedOption: SFA_LINK_OPTIONS | '';
  selectedOrgId: string;
}

export const SfaLinkBusinessToOrgForm = ({
  businessName,
  onSelectOption,
  onSelectOrg,
  linkingData,
  selectedOption,
  selectedOrgId
}: SfaLinkBusinessToOrgFormProps) => {
  const intl = useIntl();
  const [filterText, setFilterText] = useState('');

  const text = useMemo(
    () => ({
      areYouSure: intl.formatMessage({ id: 'onboarding.are_you_sure', defaultMessage: 'Are you sure?' }),
      areYouSureExistingText: intl.formatMessage(
        {
          id: 'onboarding.are_you_sure_existing_text',
          defaultMessage:
            "Make sure you've selected the right organisation to link {businessName} to before you continue. Once the link is set up, you cannot change it later."
        },
        {
          businessName
        }
      ),
      areYouSureNewText: intl.formatMessage({
        id: 'onboarding.are_you_sure_new_text',
        defaultMessage:
          'Make sure you want to create a new organisation to link to before you continue. Once the link is set up, you cannot change it later.'
      }),
      chooseOption: intl.formatMessage({ id: 'common.choose_an_option', defaultMessage: 'Choose an option' }),
      optionLink: intl.formatMessage({
        id: 'onboarding.link_to_an_existing_organisation',
        defaultMessage: 'Link to an existing Futrli organisation'
      }),
      optionLinkHelp: intl.formatMessage(
        {
          id: 'onboarding.link_to_an_existing_organisation_help',
          defaultMessage: 'Choose this option if want to link {businessName} to an existing Futrli organisation. '
        },
        {
          businessName
        }
      ),
      optionNew: intl.formatMessage({
        id: 'onboarding.create_a_new_organisation',
        defaultMessage: 'Create a new organisation'
      }),
      optionNewHelp: intl.formatMessage(
        {
          id: 'onboarding.create_a_new_organisation_help',
          defaultMessage:
            'Choose this option if you want to create a new Futrli organisation to link to {businessName}.'
        },
        {
          businessName
        }
      ),
      selectOrganisation: intl.formatMessage({
        id: 'onboarding.select_organisation',
        defaultMessage: 'Select organisation'
      })
    }),
    [businessName, intl]
  );

  const filteredOrganisations = useMemo(() => {
    const filter = deburr(filterText).toLowerCase();
    return linkingData?.organisations.filter(org => deburr(org.name).toLowerCase().includes(filter));
  }, [filterText, linkingData?.organisations]);

  return (
    <div
      data-testid="SfaLinkBusinessToOrgForm"
      style={{
        backgroundColor: 'var(--colorsUtilityMajor010)',
        border: '1px solid var(--colorsUtilityMajor100)',
        borderRadius: 'var(--borderRadius200)',
        padding: '24px',
        width: '575px'
      }}
    >
      <RadioButtonGroup
        name="linking-choice"
        legend={text.chooseOption}
        m={0}
        value={selectedOption}
        onChange={onSelectOption}
      >
        <RadioButton
          id={SFA_LINK_OPTIONS.EXISTING}
          value={SFA_LINK_OPTIONS.EXISTING}
          label={text.optionLink}
          inputHint={text.optionLinkHelp}
          checked={selectedOption === SFA_LINK_OPTIONS.EXISTING}
        />
        {selectedOption === SFA_LINK_OPTIONS.EXISTING && (
          <SfaFormIndentation pl={3} mb="12px">
            <FilterableSelect
              name="organisation"
              id="organisation"
              label={text.selectOrganisation}
              onChange={onSelectOrg}
              value={selectedOrgId}
              disableDefaultFiltering
              onFilterChange={setFilterText}
              mb={0}
            >
              {filteredOrganisations?.map(org => (
                <Option key={org.id} value={org.id} text={org.name}>
                  <Box display="flex" width="100%" alignItems="center">
                    <img
                      src="https://s3-eu-west-1.amazonaws.com/futrli-asset-store/futrli-by-sage-badge-blk.png"
                      alt=""
                      width="20"
                      height="20"
                    />
                    <Box ml="6px">{org.name}</Box>
                  </Box>
                </Option>
              ))}
            </FilterableSelect>
            {selectedOrgId && (
              <Message
                open
                variant="warning"
                title={<Typography variant="strong">{text.areYouSure}</Typography>}
                mt="12px"
                mb={0}
              >
                {text.areYouSureExistingText}
              </Message>
            )}
          </SfaFormIndentation>
        )}

        <RadioButton
          id={SFA_LINK_OPTIONS.NEW}
          value={SFA_LINK_OPTIONS.NEW}
          label={text.optionNew}
          inputHint={text.optionNewHelp}
          checked={selectedOption === SFA_LINK_OPTIONS.NEW}
        />
        {selectedOption === SFA_LINK_OPTIONS.NEW && (
          <SfaFormIndentation pl={3}>
            <Message open variant="warning" title={<Typography variant="strong">{text.areYouSure}</Typography>} mb={0}>
              {text.areYouSureExistingText}
            </Message>
          </SfaFormIndentation>
        )}
      </RadioButtonGroup>
    </div>
  );
};
